import { autocompletion, CompletionContext, CompletionResult } from "@codemirror/autocomplete";
import { Decoration, DecorationSet, EditorView, keymap, ViewPlugin, ViewUpdate, WidgetType } from "@codemirror/view";
import { StateEffect, StateField, RangeSetBuilder } from "@codemirror/state";
import type { RefObject } from "react";
import type { IUDMOperand as FetchParam, IUserDefinedMetric } from "./constants";
import { isFormulaCompatibleWellnessMetric } from "@utils/wellness_utils";

// ─── Existing utils ───────────────────────────────────────────────────────────

// The editor and in-memory formula keep operands BARE (op1) to mirror the flag
// condition editor. The metric backend (simpleeval) requires variables wrapped in
// quotation marks, so we convert at the persistence/validation boundary only.
// Quoted tokens in a metric formula are either "opN" (labels live in fetch_params)
// or a query condition string like "op1 > 0.9 * op2". Condition strings must
// pass through both transforms untouched: their opN tokens stay bare on the wire.
export const toBareFormula = (formula: string): string =>
    formula.replace(/"(op\d+)"/g, "$1");

// Splitting on quoted segments keeps every quoted string (an already-quoted "opN"
// or a condition) intact and quotes bare opN tokens only outside them, so the
// result is idempotent regardless of mixed/quoted input (never produces ""op1"").
export const toQuotedFormula = (formula: string): string =>
    formula
        .split(/("[^"]*")/)
        .map((segment) =>
            segment.startsWith('"') ? segment : segment.replace(/\bop\d+\b/g, '"$&"')
        )
        .join("");

export const extractOperands = (formula: string): string[] => {
    // \bop\d+\b matches operand tokens whether or not they are quoted (the quote
    // characters act as word boundaries), mirroring the flag condition editor so the
    // operand list and autocomplete suggestions populate the same way.
    const matches = formula.match(/\bop\d+\b/g) || [];
    return [...new Set(matches)].sort(
        (a, b) => parseInt(a.replace("op", "")) - parseInt(b.replace("op", ""))
    );
};

// One query call located in a formula: days_since(<condition>, <lookback>),
// count_true(<condition>, <lookback>) or value_to_hit_acwr(<operand>, <target>).
// The first two take a quoted condition string and an integer lookback. The third takes
// one operand (bare in the editor, quoted when stored) and a decimal target ratio. The
// backend rejects anything else, so matching them does not need a parser.
export interface QueryCall {
    fn: "days_since" | "count_true" | "value_to_hit_acwr";
    // For value_to_hit_acwr this holds the operand token (op1), so the operand counts
    // as a condition operand and keeps its label.
    condition: string;
    // 0 for value_to_hit_acwr, which has no lookback.
    lookback: number;
    target?: number;
    start: number;
    end: number;
}

const QUERY_CALL_RE = /(days_since|count_true)\s*\(\s*"([^"]*)"\s*,\s*(\d+)\s*\)/g;
const VALUE_TO_HIT_RE = /value_to_hit_acwr\s*\(\s*"?(op\d+)"?\s*,\s*(\d+\.?\d*|\.\d+)\s*\)/g;

export const extractQueryCalls = (formula: string): QueryCall[] => {
    const conditionCalls = [...formula.matchAll(QUERY_CALL_RE)].map((m): QueryCall => ({
        fn: m[1] as QueryCall["fn"],
        condition: m[2],
        lookback: parseInt(m[3], 10),
        start: m.index ?? 0,
        end: (m.index ?? 0) + m[0].length,
    }));
    const valueToHitCalls = [...formula.matchAll(VALUE_TO_HIT_RE)].map((m): QueryCall => ({
        fn: "value_to_hit_acwr",
        condition: m[1],
        lookback: 0,
        target: parseFloat(m[2]),
        start: m.index ?? 0,
        end: (m.index ?? 0) + m[0].length,
    }));
    return [...conditionCalls, ...valueToHitCalls].sort((a, b) => a.start - b.start);
};

// Everything outside every query call. Operands appearing only inside a call are
// swallowed by it — the backend resolves each call to a day count before evaluating the
// surrounding arithmetic — so only the operands left here can move a tested result.
const outsideQueryCalls = (formula: string): string => {
    let cursor = 0;
    let outside = "";
    for (const call of extractQueryCalls(formula)) {
        outside += formula.slice(cursor, call.start);
        cursor = call.end;
    }
    return outside + formula.slice(cursor);
};

// Operands that the formula uses only as the first argument of value_to_hit_acwr.
// The backend reads only the metric of such an operand, so the editor shows the metric alone.
export const metricOnlyOperands = (formula: string): Set<string> => {
    const calls = extractQueryCalls(formula);
    const usedElsewhere = new Set([
        ...extractOperands(outsideQueryCalls(formula)),
        ...calls.filter((c) => c.fn !== "value_to_hit_acwr").flatMap((c) => extractOperands(c.condition)),
    ]);
    return new Set(
        calls
            .filter((c) => c.fn === "value_to_hit_acwr" && !usedElsewhere.has(c.condition))
            .map((c) => c.condition),
    );
};

export const splitOperandsByRole = (
    formula: string,
    operands: string[],
): { valueOperands: string[]; conditionOperands: string[] } => {
    const usedOutside = new Set(extractOperands(outsideQueryCalls(formula)));
    return {
        valueOperands: operands.filter((op) => usedOutside.has(op)),
        conditionOperands: operands.filter((op) => !usedOutside.has(op)),
    };
};

// Swap each query call for the count being simulated, so a tested result
// responds to it. Without this the backend's validation stub returns a fixed sample and
// the result never moves. `dayCounts` is positional: index i is the i-th call that
// extractQueryCalls returns for the same formula.
export const substituteQueryCalls = (formula: string, dayCounts: number[]): string => {
    let cursor = 0;
    let out = "";
    extractQueryCalls(formula).forEach((call, i) => {
        out += formula.slice(cursor, call.start) + `(${dayCounts[i] ?? 0})`;
        cursor = call.end;
    });
    return out + formula.slice(cursor);
};

export const splitMetricIdentifier = (identifier: string): { base: string; stat: string } => {
    if (!identifier) return { base: "", stat: "avg" };
    const parts = identifier.split("-");
    if (parts.length < 3) return { base: identifier, stat: "avg" };
    return { base: `${parts[0]}-${parts[1]}`, stat: parts[2] || "avg" };
};

// ─── Operand label decorations ────────────────────────────────────────────────

export const startEditEffect = StateEffect.define<{ operandName: string; from: number; to: number }>();
export const confirmEditEffect = StateEffect.define<{ operandName: string; newLabel: string }>();
export const cancelEditEffect = StateEffect.define<null>();

export const editingOperandField = StateField.define<{ operandName: string; from: number; to: number } | null>({
    create: () => null,
    update: (value, tr) => {
        for (const e of tr.effects) {
            if (e.is(startEditEffect)) return e.value;
            if (e.is(confirmEditEffect) || e.is(cancelEditEffect)) return null;
        }
        return value;
    },
});

const PENCIL_SVG = `<svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7.5 1.5l3 3L4 11H1V8L7.5 1.5z"/></svg>`;
const CHECK_SVG = `<svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 6 4.5 9.5 11 2"/></svg>`;
const CLOSE_SVG = `<svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="2" y1="2" x2="10" y2="10"/><line x1="10" y1="2" x2="2" y2="10"/></svg>`;

class OperandLabelWidget extends WidgetType {
    constructor(
        private operandName: string,
        private label: string,
        private isEditing: boolean,
        private onEdit: () => void,
        private onConfirm: (newLabel: string) => void,
        private onCancel: () => void,
        private onDelete: () => void,
    ) {
        super();
    }

    eq(other: OperandLabelWidget): boolean {
        return (
            other.operandName === this.operandName &&
            other.label === this.label &&
            other.isEditing === this.isEditing
        );
    }

    toDOM(): HTMLElement {
        const wrap = document.createElement("span");
        wrap.className = "cm-operand-tag";
        wrap.setAttribute("data-operand", this.operandName);

        if (this.isEditing) {
            const input = document.createElement("input");
            input.className = "cm-operand-edit-input";
            input.value = this.label;
            input.size = Math.max(this.label.length + 2, 6);

            const handleConfirm = () => this.onConfirm(input.value.trim() || this.label);

            input.addEventListener("keydown", (e) => {
                if (e.key === "Enter") { handleConfirm(); e.preventDefault(); }
                if (e.key === "Escape") { this.onCancel(); e.preventDefault(); }
                e.stopPropagation();
            });
            input.addEventListener("mousedown", (e) => e.stopPropagation());
            input.addEventListener("click", (e) => e.stopPropagation());

            const checkBtn = document.createElement("button");
            checkBtn.type = "button";
            checkBtn.className = "cm-operand-check-btn";
            checkBtn.innerHTML = CHECK_SVG;
            checkBtn.title = "Confirm";
            checkBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); handleConfirm(); });

            const cancelBtn = document.createElement("button");
            cancelBtn.type = "button";
            cancelBtn.className = "cm-operand-cancel-btn";
            cancelBtn.innerHTML = CLOSE_SVG;
            cancelBtn.title = "Cancel";
            cancelBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); this.onCancel(); });

            wrap.appendChild(input);
            wrap.appendChild(checkBtn);
            wrap.appendChild(cancelBtn);
            setTimeout(() => { input.focus(); input.select(); }, 0);
        } else {
            const labelSpan = document.createElement("span");
            labelSpan.className = "cm-operand-label-text";
            labelSpan.textContent = this.label;

            const pencilBtn = document.createElement("button");
            pencilBtn.type = "button";
            pencilBtn.className = "cm-operand-pencil-btn";
            pencilBtn.innerHTML = PENCIL_SVG;
            pencilBtn.title = `Edit label for ${this.operandName}`;
            pencilBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); this.onEdit(); });

            const deleteBtn = document.createElement("button");
            deleteBtn.type = "button";
            deleteBtn.className = "cm-operand-delete-btn";
            deleteBtn.innerHTML = CLOSE_SVG;
            deleteBtn.title = `Remove ${this.operandName}`;
            deleteBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); this.onDelete(); });

            wrap.appendChild(labelSpan);
            wrap.appendChild(pencilBtn);
            wrap.appendChild(deleteBtn);
        }

        return wrap;
    }

    ignoreEvent() { return false; }
}

function buildDecorations(
    view: EditorView,
    fetchParams: Record<string, FetchParam>,
    onLabelConfirm: RefObject<((operandName: string, newLabel: string, from?: number, to?: number) => void) | null>,
): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    const editingState = view.state.field(editingOperandField, false);
    const doc = view.state.doc.toString();
    const regex = /\bop\d+\b/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(doc)) !== null) {
        const opName = match[0];                       // bare key for fetch_params lookup
        const label = fetchParams[opName]?.label;
        if (!label) continue;

        const chipFrom = match.index;
        const chipTo = match.index + match[0].length;

        builder.add(
            chipFrom,
            chipTo,
            Decoration.replace({
                widget: new OperandLabelWidget(
                    opName,
                    label,
                    editingState?.operandName === opName && editingState?.from === chipFrom,
                    () => view.dispatch({ effects: startEditEffect.of({ operandName: opName, from: chipFrom, to: chipTo }) }),
                    (newLabel) => {
                        view.dispatch({ effects: confirmEditEffect.of({ operandName: opName, newLabel }) });
                        onLabelConfirm.current?.(opName, newLabel, chipFrom, chipTo);
                    },
                    () => view.dispatch({ effects: cancelEditEffect.of(null) }),
                    () => {
                        // Remove all occurrences of this operand from the document
                        const currentDoc = view.state.doc.toString();
                        const re = new RegExp(`\\b${opName}\\b`, "g");
                        const changes: { from: number; to: number }[] = [];
                        let m: RegExpExecArray | null;
                        while ((m = re.exec(currentDoc)) !== null) {
                            changes.push({ from: m.index, to: m.index + opName.length });
                        }
                        if (changes.length) view.dispatch({ changes });
                    },
                ),
            }),
        );
    }

    return builder.finish();
}

const operandChipKeymap = keymap.of([
    {
        key: "Backspace",
        run: (view) => {
            const sel = view.state.selection.main;
            if (!sel.empty) return false;
            const match = view.state.doc.toString().slice(0, sel.head).match(/\bop\d+$/);
            if (!match) return false;
            view.dispatch({ changes: { from: sel.head - match[0].length, to: sel.head } });
            return true;
        },
    },
    {
        key: "Delete",
        run: (view) => {
            const sel = view.state.selection.main;
            if (!sel.empty) return false;
            const match = view.state.doc.toString().slice(sel.head).match(/^op\d+\b/);
            if (!match) return false;
            view.dispatch({ changes: { from: sel.head, to: sel.head + match[0].length } });
            return true;
        },
    },
]);

export const createOperandLabelPlugin = (
    fetchParams: Record<string, FetchParam>,
    onLabelConfirm: RefObject<((operandName: string, newLabel: string) => void) | null>,
) => [
    ViewPlugin.fromClass(
        class {
            decorations: DecorationSet;
            constructor(view: EditorView) {
                this.decorations = buildDecorations(view, fetchParams, onLabelConfirm);
            }
            update(update: ViewUpdate) {
                if (
                    update.docChanged ||
                    update.viewportChanged ||
                    update.state.field(editingOperandField, false) !==
                        update.startState.field(editingOperandField, false)
                ) {
                    this.decorations = buildDecorations(update.view, fetchParams, onLabelConfirm);
                }
            }
        },
        {
            decorations: (v) => v.decorations,
            provide: (plugin) =>
                EditorView.atomicRanges.of((view) => view.plugin(plugin)?.decorations ?? Decoration.none),
        },
    ),
    operandChipKeymap,
];

// ─── Autocomplete ─────────────────────────────────────────────────────────────

// Custom-function names in the metric formula language (FormulaEvaluatorConsts).
// Bare occurrences of these must never be turned into operands.
const FORMULA_KEYWORDS = new Set([
    "hhmmss_to_minutes", "abs", "min", "max", "pow", "sqrt",
    "days_since", "count_true", "value_to_hit_acwr",
]);
// Identifier-based keywords of the query condition language (parsed server-side
// by UserDefinedFlagUtil.parse_condition). Reserved inside a condition only — outside
// one they are ordinary words a label may use.
const CONDITION_KEYWORDS = new Set(["and", "or", "not", "var"]);
const OP_NUM_RE = /^op\d+$/;

const isReservedWord = (word: string, inQueryCondition: boolean): boolean => {
    const lower = word.toLowerCase();
    return FORMULA_KEYWORDS.has(lower) || (inQueryCondition && CONDITION_KEYWORDS.has(lower));
};

// Operand labels are display text ("Player Load") while condition tokens are typed
// bare ("playerload"), so matching a typed word to a label ignores case and every
// non-alphanumeric character.
export const normalizeOperandLabel = (label: string): string =>
    label.toLowerCase().replace(/[^a-z0-9]/g, "");

// A quote opened directly after `days_since(` or `count_true(` delimits the call's
// condition string, not an operand label. Operand tokens inside it are typed bare and
// are still discovered by extractOperands, so the quote-delimited-label completions
// (Cases 1 and 2) must stay out of the way there — the bare-word completion is
// what applies inside a condition.
const QUERY_CONDITION_PREFIX_RE = /(?:days_since|count_true)\s*\(\s*$/;
const isQueryConditionQuote = (doc: string, quoteStart: number): boolean =>
    QUERY_CONDITION_PREFIX_RE.test(doc.slice(0, quoteStart));

// Scans `text` forward tracking open/close state for each quote type independently.
// Returns the position and character of the last unmatched opening quote, or null
// when all quotes are balanced. Handles single and double quotes without mixing.
// Used only to detect a user typing a "quoted label"; inserted operands are bare
// (op1) and carry no quotes.
function findOpenQuote(text: string): { quoteChar: string; start: number } | null {
    let inDouble = false;
    let inSingle = false;
    let doubleStart = -1;
    let singleStart = -1;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"' && !inSingle) {
            if (inDouble) { inDouble = false; doubleStart = -1; }
            else { inDouble = true; doubleStart = i; }
        } else if (ch === "'" && !inDouble) {
            if (inSingle) { inSingle = false; singleStart = -1; }
            else { inSingle = true; singleStart = i; }
        }
    }
    if (inDouble) return { quoteChar: '"', start: doubleStart };
    if (inSingle) return { quoteChar: "'", start: singleStart };
    return null;
}

export type AutoCompleteCallbacks = {
    getNextOperand: () => string;
    setPendingLabel: (opName: string, label: string, quoteChar: string) => void;
    getExistingOperandForLabel: (label: string) => string | null;
    // {opN: display label} for every operand currently in the formula, so a typed
    // word can be offered as a reference to an operand that already exists.
    getOperandLabels: () => Record<string, string>;
};

// Operand-typing autocomplete mirroring the flag condition editor: typing a bare
// word, a "quoted name", or an op-prefix produces a bare opN token — either the
// existing operand whose label matches, or a new one whose label is stored as a
// pending binding (its metric is selected later in its OperandBlock). Bare tokens
// are wrapped in quotes only at the save/validation boundary (toQuotedFormula), as
// the metric backend requires.
export const createConditionAutocomplete = (
    existingOperands: string[],
    callbacksRef?: RefObject<AutoCompleteCallbacks | null>,
) => {
    const insertOperand = (
        view: EditorView,
        from: number,
        to: number,
        label: string,
        quoteChar = "",
        trailing = "",
    ) => {
        const cb = callbacksRef?.current;
        if (!cb) return;
        const existingOp = cb.getExistingOperandForLabel(label);
        const opName = existingOp ?? cb.getNextOperand();
        if (!existingOp) cb.setPendingLabel(opName, label, quoteChar);
        view.dispatch({ changes: { from, to, insert: `${opName}${trailing}` } });
    };

    const makeAddOperandOption = (label: string, quoteChar = "") => ({
        label: `Add "${label}" as operand`,
        type: "text" as const,
        detail: "new operand",
        apply: (view: EditorView, _c: unknown, f: number, t: number) =>
            insertOperand(view, f, t, label, quoteChar),
    });

    const addOperandOption = (label: string, from: number, quoteChar = "") => ({
        from,
        options: [makeAddOperandOption(label, quoteChar)],
    });

    // Operands already in the formula whose label matches the typed word. Choosing one
    // inserts its bare opN token, so the same value can be referenced by name from
    // anywhere in the formula — including inside a query condition.
    const matchingOperandOptions = (text: string) => {
        const typed = normalizeOperandLabel(text);
        const labels = callbacksRef?.current?.getOperandLabels() ?? {};
        const matches = Object.entries(labels).filter(([, label]) =>
            normalizeOperandLabel(label).startsWith(typed),
        );
        return {
            // The word already names an operand, so offering to add it would create a
            // second block for the same value.
            isExactMatch: matches.some(([, label]) => normalizeOperandLabel(label) === typed),
            options: matches.map(([opName, label]) => ({
                label,
                type: "variable" as const,
                detail: opName,
                apply: (view: EditorView, _c: unknown, f: number, t: number) =>
                    view.dispatch({ changes: { from: f, to: t, insert: opName } }),
            })),
        };
    };

    const autocompleteExt = autocompletion({
        override: [
            (context: CompletionContext): CompletionResult | null => {
                const docUpToCursor = context.state.doc.sliceString(0, context.pos);
                const openQuote = findOpenQuote(docUpToCursor);
                const inQueryCondition =
                    openQuote !== null && isQueryConditionQuote(docUpToCursor, openQuote.start);

                if (callbacksRef && !inQueryCondition) {
                    // Case 1: cursor inside an open quote — suggest while typing the label.
                    if (openQuote) {
                        const label = docUpToCursor.slice(openQuote.start + 1).trim();
                        if (label) return addOperandOption(label, openQuote.start, openQuote.quoteChar);
                    }

                    // Case 2: cursor right after a complete quoted string (both quotes present).
                    const quotedMatch = context.matchBefore(/["'][^"']*["']/);
                    if (quotedMatch) {
                        const raw = quotedMatch.text;
                        const quoteChar = raw[0];
                        if (raw.length > 2 && raw[raw.length - 1] === quoteChar) {
                            const label = raw.slice(1, -1).trim();
                            // Skip canonical "opN" tokens — those are already operands —
                            // and query condition strings, which are arguments.
                            if (
                                label &&
                                !OP_NUM_RE.test(label) &&
                                !isQueryConditionQuote(docUpToCursor, quotedMatch.from)
                            ) {
                                return addOperandOption(label, quotedMatch.from, quoteChar);
                            }
                        }
                    }
                }

                // Case 3: op-prefix → suggest existing operands + the next sequential one.
                // Numbering is always max+1 (mirroring the flag editor), so operands stay
                // sequential regardless of what number is typed. Inserted bare; quoting
                // happens at the save/validation boundary.
                const word = context.matchBefore(/op\d*/);
                if (word && (word.from !== word.to || context.explicit)) {
                    const maxNum =
                        existingOperands.length > 0
                            ? Math.max(...existingOperands.map((op) => parseInt(op.replace("op", ""), 10)))
                            : 0;
                    const ops = [...new Set([...existingOperands, `op${maxNum + 1}`])];
                    return {
                        from: word.from,
                        options: ops
                            .map((op, i) => ({
                                label: op,
                                type: "variable" as const,
                                detail: i === ops.length - 1 ? "new operand" : "operand",
                                apply: (view: EditorView, _c: unknown, f: number, t: number) =>
                                    view.dispatch({ changes: { from: f, to: t, insert: op } }),
                            })),
                    };
                }

                // Case 4: bare non-keyword identifier → matching existing operands first,
                // then "Add as operand". This also runs inside a query condition,
                // where operand tokens are typed bare: the user writes the value's name
                // ("playerload > playerload_bl") and it resolves to that operand's opN.
                if (callbacksRef) {
                    const bareWord = context.matchBefore(/[a-zA-Z_][a-zA-Z0-9_]*/);
                    if (
                        bareWord &&
                        bareWord.text.length >= 2 &&
                        !isReservedWord(bareWord.text, inQueryCondition) &&
                        !OP_NUM_RE.test(bareWord.text)
                    ) {
                        const existing = matchingOperandOptions(bareWord.text);
                        return {
                            from: bareWord.from,
                            // Options are already filtered against the typed word by
                            // matchingOperandOptions; CodeMirror's own filter compares
                            // against the option label ("Player Load"), which would drop
                            // the very matches we just selected.
                            filter: false,
                            options: existing.isExactMatch
                                ? existing.options
                                : [...existing.options, makeAddOperandOption(bareWord.text)],
                        };
                    }
                }

                return null;
            },
        ],
    });

    // Scans `text` up to `position` tracking each quote type independently so a Space
    // typed inside a "quoted name" is a literal space, not an operand-commit trigger.
    const isInsideQuotes = (text: string, position: number): boolean => {
        let inDoubleQuote = false;
        let inSingleQuote = false;
        for (let i = 0; i < position; i++) {
            if (text[i] === '"' && (i === 0 || text[i - 1] !== "\\")) {
                inDoubleQuote = !inDoubleQuote;
            } else if (text[i] === "'" && (i === 0 || text[i - 1] !== "\\")) {
                inSingleQuote = !inSingleQuote;
            }
        }
        return inDoubleQuote || inSingleQuote;
    };

    // Space after a bare non-keyword word commits it as an operand — except inside a
    // quoted name, where Space is a literal space so multi-word names can be typed.
    // A query condition is quoted but is not a name: its tokens are bare operand
    // references separated by spaces, so committing stays on inside one.
    const spaceKeymap = keymap.of([
        {
            key: "Space",
            run: (view) => {
                if (!callbacksRef?.current) return false;
                const sel = view.state.selection.main;
                if (!sel.empty) return false;
                const before = view.state.doc.sliceString(0, sel.head);
                const openQuote = findOpenQuote(before);
                const inQueryCondition =
                    openQuote !== null && isQueryConditionQuote(before, openQuote.start);
                if (!inQueryCondition && isInsideQuotes(before, sel.head)) return false;
                const match = before.match(/[a-zA-Z_][a-zA-Z0-9_]*$/);
                if (!match) return false;
                const wordText = match[0];
                if (isReservedWord(wordText, inQueryCondition) || OP_NUM_RE.test(wordText)) return false;
                // A half-typed word inside a condition is a search for an operand name,
                // not a new name — a label there can hold spaces the condition itself
                // cannot. Committing only a word that already resolves keeps Space from
                // fabricating an operand out of a prefix; anything else stays literal so
                // the completion list can finish the name.
                if (inQueryCondition && !callbacksRef.current.getExistingOperandForLabel(wordText)) {
                    return false;
                }
                insertOperand(view, sel.head - wordText.length, sel.head, wordText, "", " ");
                return true;
            },
        },
    ]);

    return [autocompleteExt, spaceKeymap];
};

// ─── Metric options builder ───────────────────────────────────────────────────

// Native metrics have a `type-<id>` unique_identifier accepted by the backend;
// user-defined metrics are referenced as `type-c<id>` and can be used as operands
// in another UDM's formula (but not their own — excluded via excludeId).
const NATIVE_IDENTIFIER_RE = /^(gps|testing|forceplate|nutrition|wellness)-\d+$/;

// Grouped options whose values are the bare metric base (no aggregation suffix).
// The operand block appends the chosen stat (e.g. "gps-123" + "-avg").
// Native metrics carry a `type-<id>` identifier; user-defined metrics are referenced
// as `type-c<id>` (synthesized here — the UDM list serializer has no unique_identifier).
// A UDM may be used as an operand in another UDM's formula, except itself (excludeId).
// A UDM is grouped by its `metric_type` so it appears in the same category as the
// native metrics of that type (e.g. a `gps` UDM lands under "Gps"); a UDM whose
// metric_type has no native group gets its own group at the end.
type MetricOption = { label: string; value: string };
type MetricGroup = { type: string; label: string; options: MetricOption[] };

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export const buildMetricOptions = (
    allMetricTypes: Record<string, any[]>,
    userDefinedMetrics: IUserDefinedMetric[] = [],
    excludeId?: number,
) => {
    const groups: MetricGroup[] = Object.entries(allMetricTypes)
        .map(([type, metrics]) => ({
            type,
            label: capitalize(type),
            options: (metrics ?? [])
                .filter((m: any) => NATIVE_IDENTIFIER_RE.test(m.unique_identifier))
                .filter((m: any) => type !== "wellness" || isFormulaCompatibleWellnessMetric(m))
                .map((m: any) => ({ label: m.name, value: m.unique_identifier })),
        }))
        .filter((group) => group.options.length > 0);

    const groupByType = new Map<string, MetricGroup>(groups.map((g) => [g.type, g]));

    userDefinedMetrics
        .filter((m) => m.id != null && m.id !== excludeId)
        .forEach((m) => {
            const option: MetricOption = { label: m.name, value: `${m.metric_type}-c${m.id}` };
            let group = groupByType.get(m.metric_type);
            if (!group) {
                group = { type: m.metric_type, label: capitalize(m.metric_type), options: [] };
                groupByType.set(m.metric_type, group);
                groups.push(group);
            }
            group.options.push(option);
        });

    return groups.map(({ label, options }) => ({ label, options }));
};
